import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, AlertTriangle, Clock, Bell, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const headers = () => {
  const t = localStorage.getItem('auth_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/**
 * EstateVaultStatusStrip
 * Calm, single-line status pill on the Overview tab. Hidden entirely when:
 *   - the user has never activated Continuity System, or
 *   - the vault has been revoked.
 * Lives above the Cashflow Calendar block. Click → Settings tab.
 */
const EstateVaultStatusStrip = ({ onGoToSettings }) => {
  const [vault, setVault] = useState(null);
  const [snapAgeDays, setSnapAgeDays] = useState(null);
  const [snapState, setSnapState] = useState('unknown'); // 'unknown' | 'none' | 'fresh' | 'stale'
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/estate-vault`, { headers: headers() });
      if (!r.ok) return;
      const data = await r.json();
      setVault(data);

      if (data.exists && data.status !== 'revoked') {
        try {
          const s = await fetch(`${BACKEND_URL}/api/estate-vault/snapshots`, { headers: headers() });
          if (!s.ok) {
            setSnapState('unknown');
          } else {
            const sj = await s.json();
            const snaps = sj.snapshots || [];
            if (snaps.length === 0) {
              setSnapState('none');
              setSnapAgeDays(null);
            } else {
              const ts = new Date(snaps[0].generated_at);
              const days = Math.floor((Date.now() - ts.getTime()) / (1000 * 60 * 60 * 24));
              setSnapAgeDays(days);
              setSnapState(days >= 30 ? 'stale' : 'fresh');
            }
          }
        } catch (_) { setSnapState('unknown'); }
      }
    } catch (_) { /* swallow — widget stays hidden on error */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!vault || !vault.exists) return null;
  if (vault.status === 'revoked') return null;

  const open = () => {
    if (onGoToSettings) onGoToSettings();
    else navigate('/dashboard/settings');
  };

  // Decide the message and tone
  let tone = 'emerald';
  let icon = <ShieldCheck className="w-4 h-4" />;
  let line = 'Continuity System: on';
  let detail = 'Your summary is current.';
  let cta = 'Review';

  if (vault.status === 'paused') {
    tone = 'slate';
    icon = <Clock className="w-4 h-4" />;
    line = 'Continuity System is paused';
    detail = 'Check-ins are not being sent. Resume when ready.';
    cta = 'Resume';
  } else if ((vault.dms_stage || 0) > 0) {
    tone = vault.dms_stage >= 4 ? 'rose' : 'orange';
    icon = <Bell className="w-4 h-4" />;
    line = `A check-in is overdue (stage ${vault.dms_stage}/${vault.dms_total_stages || 5})`;
    detail = 'Please confirm you are well to halt the cascade.';
    cta = 'Respond';
  } else if (vault.claim_pending) {
    tone = 'rose';
    icon = <AlertTriangle className="w-4 h-4" />;
    line = 'A Trusted Contact has filed an information-transfer claim';
    detail = 'Our team is reviewing. You may halt this by responding to your next check-in.';
    cta = 'View activity';
  } else if (snapState === 'none') {
    tone = 'amber';
    icon = <AlertTriangle className="w-4 h-4" />;
    line = 'No Continuity Records generated yet';
    detail = 'Trusted Contacts would receive nothing until you create your first Continuity Records.';
    cta = 'Create now';
  } else if (snapState === 'stale') {
    tone = 'amber';
    icon = <AlertTriangle className="w-4 h-4" />;
    line = `Your Continuity Records are ${snapAgeDays} days old`;
    detail = 'Refresh them if your portfolio has changed meaningfully.';
    cta = 'Refresh';
  } else if (snapState === 'fresh') {
    detail = `Summary refreshed ${snapAgeDays === 0 ? 'today' : `${snapAgeDays} day${snapAgeDays === 1 ? '' : 's'} ago`}.`;
  } else {
    // snapState === 'unknown' (couldn't fetch snapshots) — stay neutral
    detail = 'Open settings to check your summary.';
  }

  const toneClasses = {
    emerald: 'border-emerald-200 bg-emerald-50/60 text-emerald-900',
    amber: 'border-amber-200 bg-amber-50 text-amber-900',
    orange: 'border-orange-200 bg-orange-50 text-orange-900',
    rose: 'border-rose-300 bg-rose-50 text-rose-900',
    slate: 'border-slate-200 bg-slate-50 text-slate-700',
  };
  const iconColour = {
    emerald: 'text-emerald-700', amber: 'text-amber-700',
    orange: 'text-orange-700', rose: 'text-rose-700', slate: 'text-slate-500',
  };

  return (
    <button
      type="button"
      onClick={open}
      className={`w-full flex items-center justify-between gap-3 border ${toneClasses[tone]} rounded-lg px-3 py-2 text-left hover:shadow-xs transition-shadow`}
      data-testid="estate-vault-status-strip"
    >
      <div className="flex items-center gap-2 min-w-0">
        <span className={iconColour[tone]}>{icon}</span>
        <span className="text-sm font-medium truncate">{line}</span>
        <span className="text-xs opacity-80 hidden sm:inline truncate">&middot; {detail}</span>
      </div>
      <div className="flex items-center gap-1 text-xs font-medium shrink-0">
        {cta} <ChevronRight className="w-3.5 h-3.5" />
      </div>
    </button>
  );
};

export default EstateVaultStatusStrip;
